/**
 * How a board applies a card's placement: as a removal from every cell the card is in that
 * it should no longer be in, and an addition to every cell it should be in and is not.
 *
 * **Never an atomic move.** A cell address is a pair, and a field on either axis can be
 * multi-valued, so one card can sit in several cells at once and a gesture can take it out
 * of one and into two. Removals and additions are sets, which makes a sideways drag, a
 * diagonal one and a multi-valued change the same three lines of arithmetic.
 *
 * The same function serves the pending overlay and the confirmed fold, because the two carry
 * the same kind of statement: these are the buckets the card is in now. That is what makes a
 * successful write settle without the card flickering back through where it came from.
 */

import type {BoardCell, BoardColumn, BoardData, BoardRow, CellAddress} from '@github-ui/board-core'
import type {
  DeltaEntityFieldValuesOperation,
  DocumentAdapterResult,
  EntityFieldItemOperation,
  PendingConnectionOperation,
  SetFieldsOperation,
  UpdateEntityFieldItemsOperation,
} from '@github-ui/optimistic-connection'

import {type BoardPlacement, readBoardPlacement} from './board-placement'
import type {
  SavedViewsBoardAdapter,
  SavedViewsBoardData,
  SavedViewsBoardItem,
  SavedViewsBoardItemAxisMove,
} from './types'

type BoardItem = SavedViewsBoardItem
type BoardItemData<TItem extends BoardItem> = BoardData<TItem>
type BoardPayloadDocument<TItem extends BoardItem, TMetadata> = {
  boardData: SavedViewsBoardData<TItem>
  metadata: TMetadata
}
type UpdateMovedCard<TItem extends BoardItem> = (card: TItem, move: SavedViewsBoardItemAxisMove) => TItem

/**
 * Cells grouped by column, then row. A lookup reads both keys as they are, so nothing is
 * encoded per cell and no two addresses can collide.
 */
type CellSet = ReadonlyMap<string, ReadonlySet<string>>

function toCellSet(addresses: readonly CellAddress[]): CellSet {
  const set = new Map<string, Set<string>>()
  for (const {columnKey, rowKey} of addresses) {
    const rows = set.get(columnKey)
    if (rows === undefined) set.set(columnKey, new Set([rowKey]))
    else rows.add(rowKey)
  }
  return set
}

function hasCell(set: CellSet, {columnKey, rowKey}: CellAddress): boolean {
  return set.get(columnKey)?.has(rowKey) ?? false
}

/**
 * Checks the structure every board payload has. The item and metadata types come from the board
 * that registered the adapter, which only reads its own query.
 */
export function isBoardPayloadDocument<TItem extends BoardItem = BoardItem, TMetadata = unknown>(
  value: unknown,
): value is BoardPayloadDocument<TItem, TMetadata> {
  if (value == null || typeof value !== 'object') return false
  const {boardData} = value as {boardData?: Partial<SavedViewsBoardData<BoardItem>>}
  if (boardData == null || typeof boardData !== 'object') return false

  const {data} = boardData
  return data != null && Array.isArray(data.cells) && Array.isArray(data.columns) && Array.isArray(data.rows)
}

/** An edit made outside the board, such as in the side panel, to one item's fields. */
export type BoardItemEdit =
  | SetFieldsOperation
  | EntityFieldItemOperation
  | UpdateEntityFieldItemsOperation
  | DeltaEntityFieldValuesOperation
export type ApplyItemEdit<TItem extends BoardItem> = (item: TItem, edit: BoardItemEdit) => TItem

type BoardDocumentAdapterHooks<TItem extends BoardItem, TMetadata> = Pick<
  SavedViewsBoardAdapter<TItem, TMetadata>,
  'updateMovedItem'
> & {
  /** Applies an outside edit to one card. Returns the same card when nothing applies. */
  applyItemEdit?: ApplyItemEdit<TItem>
}

/**
 * Builds the board's document adapter.
 *
 * A placement moves the card, and `updateMovedItem` gives a moved card what it reads in its new
 * buckets. Any other field edit, such as one from the side panel, goes to `applyItemEdit` to
 * update the card where it is. A field edit never moves a card here: that needs the card's new
 * bucket keys, which only the server can give.
 *
 * Without `applyItemEdit`, the adapter declines every edit that is not a placement, and the rest
 * of a card comes from the next board read.
 */
export function createBoardDocumentAdapter<TItem extends BoardItem = BoardItem, TMetadata = unknown>({
  updateMovedItem,
  applyItemEdit,
}: BoardDocumentAdapterHooks<TItem, TMetadata> = {}) {
  return function applyBoardOperation<T>(document: T, change: PendingConnectionOperation): DocumentAdapterResult<T> {
    const {operation} = change
    if (!isBoardItemEdit(operation)) return undefined

    const placement = operation.type === 'setFields' ? readBoardPlacement(operation) : undefined
    if (placement === undefined && applyItemEdit === undefined) return undefined
    if (!isBoardPayloadDocument<TItem, TMetadata>(document)) return undefined

    let boardData: SavedViewsBoardData<TItem> | undefined
    if (placement !== undefined) {
      const {metadata} = document
      const updateMovedCard: UpdateMovedCard<TItem> | undefined =
        updateMovedItem && ((card, move) => updateMovedItem(card, move, metadata))
      boardData = placeCard(document.boardData, operation.id, placement, updateMovedCard)
    } else if (applyItemEdit !== undefined) {
      boardData = updateCardEverywhere(document.boardData, operation.id, item => applyItemEdit(item, operation))
    }
    if (boardData === undefined) return undefined
    if (boardData === document.boardData) return {document}

    return {document: {...document, boardData}}
  }
}

/**
 * Whether an operation edits the fields of a card the board holds, such as from the side panel.
 * A placement is a move rather than a field edit, and is excluded.
 */
export function isFieldEditToBoardCard(operation: PendingConnectionOperation['operation'], document: unknown): boolean {
  if (!isBoardItemEdit(operation)) return false
  if (readBoardPlacement(operation) !== undefined) return false
  if (!isBoardPayloadDocument(document)) return false
  return document.boardData.data.cells.some(cell => cell.items.some(item => item.globalId === operation.id))
}

export function isBoardItemEdit(operation: PendingConnectionOperation['operation']): operation is BoardItemEdit {
  return (
    operation.type === 'setFields' ||
    operation.type === 'upsertEntityFieldItem' ||
    operation.type === 'removeEntityFieldItem' ||
    operation.type === 'updateEntityFieldItems' ||
    operation.type === 'deltaEntityFieldValues'
  )
}

/** Places cards and leaves the rest of each card as the board read it. */
export const applyBoardPlacement = createBoardDocumentAdapter()

/**
 * Updates every copy of a card: on a multi-valued axis, one card sits in several cells.
 *
 * `undefined` when the card is not on this board, so an edit to an issue somewhere else leaves
 * the board's identity alone. The same board back when the card is here and nothing changed.
 */
function updateCardEverywhere<TItem extends BoardItem>(
  boardData: SavedViewsBoardData<TItem>,
  itemId: string,
  update: (item: TItem) => TItem,
): SavedViewsBoardData<TItem> | undefined {
  let found = false
  let changed = false
  // Each cell holds the same card object, so it is updated once and shared again.
  const updated = new Map<TItem, TItem>()

  const cells = boardData.data.cells.map(cell => {
    const item = cell.items.find(candidate => candidate.globalId === itemId)
    if (item === undefined) return cell
    found = true

    let next = updated.get(item)
    if (next === undefined) {
      next = update(item)
      updated.set(item, next)
    }
    if (next === item) return cell

    changed = true
    return {...cell, items: cell.items.map(candidate => (candidate === item ? next : candidate))}
  })

  if (!found) return undefined
  if (!changed) return boardData

  return {...boardData, data: {...boardData.data, cells}}
}

function placeCard<TItem extends BoardItem>(
  boardData: SavedViewsBoardData<TItem>,
  itemId: string,
  placement: BoardPlacement,
  updateCard: UpdateMovedCard<TItem> | undefined,
): SavedViewsBoardData<TItem> | undefined {
  const {columnField, rowField, data} = boardData
  // A board with no grouping has no cells to place a card in (D29).
  if (columnField == null) return undefined

  const placedColumns = placement.axes[columnField]?.keys
  const placedRows = rowField == null ? undefined : placement.axes[rowField]?.keys
  // An operation naming neither of this board's axes is about a board arranged by other
  // fields, which is not a placement this one can read.
  if (placedColumns === undefined && placedRows === undefined) return undefined

  const {card, held} = findCard(data, itemId)
  // The card is not on this board. A placement cannot be applied to a cell nobody is
  // holding, and guessing one from the operation would invent a card the read never sent.
  if (card === undefined) return undefined

  // An axis the operation does not name is left where it is. That is how a sideways drag
  // settles: the write reports the column it changed, and the row comes from the board.
  const heldColumns = distinct(held.map(cell => cell.columnKey))
  const heldRows = distinct(held.map(cell => cell.rowKey))
  const columnKeys = placedColumns ?? heldColumns
  const rowKeys = placedRows ?? heldRows

  const destination = columnKeys.flatMap(columnKey => rowKeys.map(rowKey => ({columnKey, rowKey})))
  const destinationSet = toCellSet(destination)
  const heldSet = toCellSet(held)

  // `held` only sees loaded cards. A card already on an unfetched page of a destination cell
  // is counted twice until the next board read. A stale index looks the same and needs the count.
  const removals = held.filter(cell => !hasCell(destinationSet, cell))
  const additions = destination.filter(address => !hasCell(heldSet, address))
  // Already where the placement says it goes. Affected, and nothing to change.
  if (removals.length === 0 && additions.length === 0) return boardData

  let moved = card
  // Only for an axis whose buckets changed. A diagonal drag names both axes, and a bucket the
  // card stays in can hold a value other than its own, so it says nothing new about the card.
  if (updateCard !== undefined && !sameKeys(heldColumns, columnKeys)) {
    moved = updateCard(moved, {axis: 'columns', keys: columnKeys})
  }
  if (updateCard !== undefined && !sameKeys(heldRows, rowKeys)) {
    moved = updateCard(moved, {axis: 'rows', keys: rowKeys})
  }
  const kept = moved === card ? [] : held.filter(cell => hasCell(destinationSet, cell))

  return {
    ...boardData,
    data: {
      ...data,
      cells: nextCells(data, {itemId, card: moved, removals, additions, kept}),
      // A column shows the count it was given, so the count moves with the card rather than
      // being re-derived from the cells now under it.
      columns: withAxisCounts(
        data.columns,
        axisDelta(
          held.map(cell => cell.columnKey),
          columnKeys,
        ),
      ),
      rows: withAxisCounts(
        data.rows,
        axisDelta(
          held.map(cell => cell.rowKey),
          rowKeys,
        ),
      ),
    },
  }
}

/** The card, and every cell currently holding it. A multi-valued axis holds it in several. */
function findCard<TItem extends BoardItem>(
  data: BoardItemData<TItem>,
  itemId: string,
): {card?: TItem; held: CellAddress[]} {
  let card: TItem | undefined
  const held: CellAddress[] = []

  for (const cell of data.cells) {
    const found = cell.items.find(item => item.globalId === itemId)
    if (found === undefined) continue
    card ??= found
    held.push({columnKey: cell.columnKey, rowKey: cell.rowKey})
  }

  return {card, held}
}

/**
 * Every cell the board is currently drawing one card in.
 *
 * Read when a gesture is turned into an operation, because what the card holds on the axis it
 * did not cross has to survive the move, and only the board knows it.
 */
export function heldCells(boardData: SavedViewsBoardData<BoardItem>, itemId: string): CellAddress[] {
  return findCard(boardData.data, itemId).held
}

function nextCells<TItem extends BoardItem>(
  {cells, columns, rows}: BoardItemData<TItem>,
  {
    itemId,
    card,
    removals,
    additions,
    kept,
  }: {
    itemId: string
    card: TItem
    removals: readonly CellAddress[]
    additions: readonly CellAddress[]
    /** The cells the card stays in, which get `card` when the move changed it. */
    kept: readonly CellAddress[]
  },
): Array<BoardCell<TItem>> {
  // Runs over every cell on each drop, so a cell the move does not touch costs three `Map`
  // reads and allocates nothing.
  const removed = toCellSet(removals)
  const added = toCellSet(additions)
  const replaced = toCellSet(kept)
  const sent: CellAddress[] = []

  const next = cells.map(cell => {
    if (hasCell(removed, cell)) return withoutCard(cell, itemId)
    if (hasCell(replaced, cell)) return withReplacedCard(cell, card)
    if (!hasCell(added, cell)) return cell
    sent.push(cell)
    return withCard(cell, card)
  })

  // A cell the read payload never sent is an empty pair, so the card arrives in a cell of
  // its own. Only on a loaded column and row: the board draws no other cell, and a count of
  // one there would be a number the board made up.
  const sentSet = toCellSet(sent)
  for (const address of additions) {
    if (hasCell(sentSet, address)) continue
    if (!columns.some(column => column.key === address.columnKey)) continue
    if (!rows.some(row => row.key === address.rowKey)) continue
    next.push({...address, items: [card], totalCount: 1, hasMoreItems: false})
  }

  return next
}

function withoutCard<TItem extends BoardItem>(cell: BoardCell<TItem>, itemId: string): BoardCell<TItem> {
  const items = cell.items.filter(item => item.globalId !== itemId)
  if (items.length === cell.items.length) return cell

  return {...cell, items, totalCount: decrement(cell.totalCount)}
}

function withReplacedCard<TItem extends BoardItem>(cell: BoardCell<TItem>, card: TItem): BoardCell<TItem> {
  return {...cell, items: cell.items.map(item => (item.globalId === card.globalId ? card : item))}
}

function withCard<TItem extends BoardItem>(cell: BoardCell<TItem>, card: TItem): BoardCell<TItem> {
  if (cell.items.some(item => item.globalId === card.globalId)) return cell

  // At the top of the cell rather than the bottom. A cell pages, so its end sits below cards
  // nobody has fetched, and the top is the one position a reader is certain to see the card
  // land in. The next board read puts it where the view's sort says it belongs.
  return {...cell, items: [card, ...cell.items], totalCount: increment(cell.totalCount)}
}

/** What one axis's counts owe the move: a card leaving a bucket, or arriving in one. */
function axisDelta(before: readonly string[], after: readonly string[]): ReadonlyMap<string, number> {
  const held = new Set(before)
  const placed = new Set(after)
  const delta = new Map<string, number>()

  for (const key of held) if (!placed.has(key)) delta.set(key, -1)
  for (const key of placed) if (!held.has(key)) delta.set(key, 1)

  return delta
}

function withAxisCounts<TEntry extends BoardColumn | BoardRow>(
  axis: readonly TEntry[],
  delta: ReadonlyMap<string, number>,
): readonly TEntry[] {
  if (delta.size === 0) return axis

  let changed = false
  const next = axis.map(entry => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- `key` here is the axis entry's wire-contract key, not a keyboard event's
    const step = delta.get(entry.key)
    if (step === undefined || entry.totalCount === undefined) return entry
    changed = true
    return {...entry, totalCount: step < 0 ? decrement(entry.totalCount) : increment(entry.totalCount)}
  })

  return changed ? next : axis
}

/** A count nobody stated stays unstated rather than becoming one the board invented. */
function increment(totalCount: number | undefined): number | undefined {
  return totalCount === undefined ? undefined : totalCount + 1
}

function decrement(totalCount: number | undefined): number | undefined {
  return totalCount === undefined ? undefined : Math.max(0, totalCount - 1)
}

function distinct(keys: readonly string[]): string[] {
  return [...new Set(keys)]
}

/** Whether two lists name the same buckets. `held` is already distinct. */
function sameKeys(held: readonly string[], placed: readonly string[]): boolean {
  const placedSet = new Set(placed)
  return held.length === placedSet.size && held.every(key => placedSet.has(key))
}
